// Character definitions for the contact book/character selector

import { getSantaVideoProvider, getToothFairyVideoProvider, getReyesVideoProvider } from '@/lib/feature-flags'
import { getGenericCharacterById, isGenericCharacterId, toCharacter } from '@/lib/generic-characters'
import { CUSTOM_CHARACTER_ID } from '@/server/domain/custom-character'

export interface Character {
  id: string
  name: string // Internal identifier (e.g., 'santa', 'melchor')
  displayName: string // Display name (e.g., 'Papá Noel', 'Melchor')
  avatarId?: string // HeyGen avatar ID (for future use, currently all use same)
  callType: 'video' | 'audio'
  icon: string // Emoji or icon identifier
  personKey: string // Groups the audio/video variants of the same person (e.g., 'santa')
}

export const CHARACTERS: Character[] = [
  // Hada
  {
    id: 'tooth-fairy-video',
    name: 'tooth-fairy-video',
    displayName: 'Hada de los Dientes',
    avatarId: 'tooth-fairy-anam-avatar', // Placeholder for ANAM avatar ID
    callType: 'video',
    icon: '🧚‍♀️',
    personKey: 'tooth-fairy',
  },
  {
    id: 'tooth-fairy-audio',
    name: 'tooth-fairy-audio',
    displayName: 'Hada de los Dientes',
    callType: 'audio',
    icon: '🧚‍♀️',
    personKey: 'tooth-fairy',
  },
  {
    id: 'raton-perez-video',
    name: 'raton-perez-video',
    displayName: 'Ratón Pérez',
    avatarId: 'raton-perez-runway-avatar', // Placeholder for Runway avatar ID
    callType: 'video',
    icon: '🐭',
    personKey: 'raton-perez',
  },
  {
    id: 'raton-perez-audio',
    name: 'raton-perez-audio',
    displayName: 'Ratón Pérez',
    callType: 'audio',
    icon: '🐭',
    personKey: 'raton-perez',
  },
  // Santa
  {
    id: 'santa',
    name: 'santa',
    displayName: 'Papá Noel',
    callType: 'video',
    icon: '🎅',
    personKey: 'santa',
  },
  {
    id: 'santa-audio',
    name: 'santa-audio',
    displayName: 'Papá Noel (audio)',
    callType: 'audio',
    icon: '🎅',
    personKey: 'santa',
  },
  // Reyes Video
  {
    id: 'melchor-video',
    name: 'melchor-video',
    displayName: 'Melchor',
    callType: 'video',
    icon: '👑',
    personKey: 'melchor',
  },
  {
    id: 'gaspar-video',
    name: 'gaspar-video',
    displayName: 'Gaspar',
    callType: 'video',
    icon: '👑',
    personKey: 'gaspar',
  },
  {
    id: 'baltasar-video',
    name: 'baltasar-video',
    displayName: 'Baltasar',
    callType: 'video',
    icon: '👑',
    personKey: 'baltasar',
  },
  // Reyes Audio
  {
    id: 'melchor',
    name: 'melchor',
    displayName: 'Melchor',
    callType: 'audio',
    icon: '👑',
    personKey: 'melchor',
  },
  {
    id: 'gaspar',
    name: 'gaspar',
    displayName: 'Gaspar',
    callType: 'audio',
    icon: '👑',
    personKey: 'gaspar',
  },
  {
    id: 'baltasar',
    name: 'baltasar',
    displayName: 'Baltasar',
    callType: 'audio',
    icon: '👑',
    personKey: 'baltasar',
  },
]

export function getCharacterById(id: string): Character | undefined {
  // El catálogo genérico también resuelve por id (p.ej. el flujo returnTo del
  // dashboard); su visibilidad la gatea el flag, no esta resolución.
  const generic = getGenericCharacterById(id)
  if (generic) return toCharacter(generic)
  return CHARACTERS.find((char) => char.id === id)
}

export const REYES_CHARACTER_IDS = ['melchor', 'gaspar', 'baltasar'] as const
export const REYES_VIDEO_CHARACTER_IDS = ['melchor-video', 'gaspar-video', 'baltasar-video'] as const
export const REYES_CALL_ROUTE = '/child-reyes'
export const TOOTH_FAIRY_CHARACTER_IDS = ['tooth-fairy-audio', 'tooth-fairy-video'] as const
export const RATON_PEREZ_CHARACTER_IDS = ['raton-perez-audio', 'raton-perez-video'] as const

export function isReyesCharacterId(id: string): boolean {
  return REYES_CHARACTER_IDS.includes(id as (typeof REYES_CHARACTER_IDS)[number])
}

export function isReyesVideoCharacterId(id: string): boolean {
  return REYES_VIDEO_CHARACTER_IDS.includes(id as (typeof REYES_VIDEO_CHARACTER_IDS)[number])
}

export function isToothFairyCharacterId(id: string): boolean {
  return TOOTH_FAIRY_CHARACTER_IDS.includes(id as (typeof TOOTH_FAIRY_CHARACTER_IDS)[number])
}

export function isRatonPerezCharacterId(id: string): boolean {
  return RATON_PEREZ_CHARACTER_IDS.includes(id as (typeof RATON_PEREZ_CHARACTER_IDS)[number])
}

export interface CharacterImageSources {
  webp?: string
  src: string
}

const SANTA_IMAGE: CharacterImageSources = { src: '/papanoel-avatar.png' }
const TOOTH_FAIRY_IMAGE: CharacterImageSources = { webp: '/hada/hada.webp', src: '/hada/hada.png' }
const RATON_PEREZ_IMAGE: CharacterImageSources = {
  webp: '/raton-perez/raton-perez.webp',
  src: '/raton-perez/raton-perez.png',
}

const IMAGE_SOURCES_BY_ID: Record<string, CharacterImageSources> = {
  santa: SANTA_IMAGE,
  'santa-audio': SANTA_IMAGE,
  'tooth-fairy-audio': TOOTH_FAIRY_IMAGE,
  'tooth-fairy-video': TOOTH_FAIRY_IMAGE,
  'raton-perez-audio': RATON_PEREZ_IMAGE,
  'raton-perez-video': RATON_PEREZ_IMAGE,
}

/** Avatar image sources (WebP + fallback) for a character, by id. */
export function getCharacterImageSources(characterId: string): CharacterImageSources {
  const generic = getGenericCharacterById(characterId)
  if (generic) {
    return generic.imageSources
  }
  const known = IMAGE_SOURCES_BY_ID[characterId]
  if (known) {
    return known
  }
  // Reyes: la imagen comparte id con la variante audio (melchor-video → melchor).
  const imageId = characterId.endsWith('-video')
    ? characterId.replace('-video', '')
    : characterId
  return { webp: `/reyes/${imageId}.webp`, src: `/reyes/${imageId}.png` }
}

/**
 * Optimized preview clip for the character selector cards, keyed by the
 * character id (the video-capable variant). Returns `undefined` when no clip
 * has been published so the card falls back to the static avatar.
 * Assets: `public/videos/previews/<personKey>.mp4` (see
 * `scripts/generate-card-previews.sh`).
 */
const PREVIEW_VIDEO_BY_ID: Record<string, string> = {
  santa: '/videos/previews/santa.mp4',
  'tooth-fairy-video': '/videos/previews/tooth-fairy.mp4',
  'raton-perez-video': '/videos/previews/raton-perez.mp4',
  'melchor-video': '/videos/previews/melchor.mp4',
  'baltasar-video': '/videos/previews/baltasar.mp4',
}

export function getCharacterPreviewVideo(characterId: string): string | undefined {
  return getGenericCharacterById(characterId)?.previewVideoSrc ?? PREVIEW_VIDEO_BY_ID[characterId]
}

/**
 * Personajes con camino Runway conmutable por flag: con su flag fuera del
 * legacy (HeyGen/ANAM) la llamada va a la página dedicada, que elige el
 * transporte runway|livekit según el mismo flag (ADR-012); el default deja
 * intacto el camino legacy del personaje. El Ratón Pérez no está aquí: su
 * página dedicada ya es la ruta por defecto (sin legacy que conmutar).
 */
const RUNWAY_ROUTE_OVERRIDES = [
  {
    id: 'santa',
    usesRunway: () => getSantaVideoProvider() !== 'heygen',
    route: '/child-santa-video',
  },
  {
    id: 'tooth-fairy-video',
    usesRunway: () => getToothFairyVideoProvider() !== 'anam',
    route: '/child-tooth-fairy-video-runway',
  },
  {
    id: 'melchor-video',
    usesRunway: () => getReyesVideoProvider() !== 'anam',
    route: '/child-melchor-video-runway',
  },
  {
    id: 'baltasar-video',
    usesRunway: () => getReyesVideoProvider() !== 'anam',
    route: '/child-baltasar-video-runway',
  },
] as const

/**
 * The child-mode route to navigate to when starting a call with a character.
 *
 * Con `trial: true` el vídeo va SIEMPRE por la página dedicada, ignorando los
 * flags de proveedor: los legacy (HeyGen/ANAM) no cortan la sesión server-side
 * a los segundos del trial, y la página dedicada solo usa transportes que sí
 * cortan (runway o livekit, ver toVideoTransport en feature-flags). Las
 * llamadas de pago respetan el flag.
 */
function findRunwayRouteOverride(character: Character, trial: boolean) {
  return RUNWAY_ROUTE_OVERRIDES.find(
    (override) => override.id === character.id && (trial || override.usesRunway())
  )
}

export function getCharacterCallRoute(
  character: Character,
  options?: { trial?: boolean }
): string {
  // Catálogo genérico: página dinámica única con transporte livekit fijo
  // (corta la sesión server-side, así que también sirve para el trial).
  if (isGenericCharacterId(character.id)) {
    return `/child-generic/${character.id}`
  }
  const runwayOverride = findRunwayRouteOverride(character, options?.trial ?? false)
  if (runwayOverride) return runwayOverride.route
  // Dedicated child pages per character (legacy/default providers).
  const dedicatedRoutes: Record<string, string> = {
    'santa-audio': '/child-santa-audio',
    'tooth-fairy-audio': '/child-tooth-fairy-audio',
    'raton-perez-audio': '/child-raton-perez-audio',
    'raton-perez-video': '/child-raton-perez-video',
    'gaspar-video': '/child-gaspar-video',
    'tooth-fairy-video': '/child-tooth-fairy-video',
    [CUSTOM_CHARACTER_ID]: '/child-custom',
  }
  const dedicated = dedicatedRoutes[character.id]
  if (dedicated) return dedicated
  if (isReyesCharacterId(character.id) || isReyesVideoCharacterId(character.id)) return REYES_CALL_ROUTE
  return '/child'
}
